import React from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../../../design/avatar";
import { Badge } from "../../../design/badge";
import { getInitials } from "../../../utils/string";
import { Supervisor } from "@visitproof/types";
import {
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  UserCircleIcon,
} from "@heroicons/react/20/solid";
import moment from "moment";

interface SupervisorGridProps {
  supervisors: Supervisor[];
}

const SupervisorGrid: React.FC<SupervisorGridProps> = ({ supervisors }) => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {supervisors.map((supervisor) => (
        <SupervisorCard key={supervisor.id} supervisor={supervisor} />
      ))}
    </div>
  );
};

interface SupervisorCardProps {
  supervisor: Supervisor;
}

const SupervisorCard: React.FC<SupervisorCardProps> = ({ supervisor }) => {
  const fullName = `${supervisor.firstName} ${supervisor.lastName}`;
  const isActive = supervisor.status !== "inactive";

  return (
    <Link
      to={`/supervisor/${supervisor.id}`}
      className="group block p-5 bg-white border rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 dark:bg-zinc-900 dark:border-zinc-700 dark:hover:border-blue-600"
    >
      {/* Header with Avatar */}
      <div className="flex items-start gap-4 mb-4">
        <Avatar
          src={supervisor.avatarUrl}
          className="size-14 ring-2 ring-zinc-100 dark:ring-zinc-800"
          initials={getInitials(fullName)}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
            {fullName}
          </h3>
          <Badge
            color={isActive ? "green" : "zinc"}
            className="mt-1"
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>

      {/* Contact Info */}
      <div className="space-y-2 mb-4">
        {supervisor.phoneNumber && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <PhoneIcon className="w-4 h-4 text-zinc-400" />
            <span className="truncate">{supervisor.phoneNumber}</span>
          </div>
        )}
        {supervisor.email && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <EnvelopeIcon className="w-4 h-4 text-zinc-400" />
            <span className="truncate">{supervisor.email}</span>
          </div>
        )}
      </div>

      {/* Employment Info */}
      <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2 text-sm">
          <CalendarIcon className="w-4 h-4 text-zinc-400" />
          <span className="text-zinc-600 dark:text-zinc-400">
            {supervisor.dateOfEmployment
              ? `Hired ${moment(supervisor.dateOfEmployment.toDate()).format("MMM D, YYYY")}`
              : supervisor.createdAt ? `Added ${moment(supervisor.createdAt.toDate()).format("MMM D, YYYY")}` : "-"}
          </span>
        </div>
        {supervisor.userId && (
          <div className="flex items-center gap-2 mt-2 text-sm">
            <UserCircleIcon className="w-4 h-4 text-green-500" />
            <span className="text-green-600 dark:text-green-400">Account linked</span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default SupervisorGrid;
